import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMyWalletAccess } from '~/features/my-wallet/composables/useMyWalletAccess'

export interface HeaderTabMenuItem {
  id: string
  label: string
  active?: boolean
  onClick: () => void
}

interface TabDefinition {
  id: HeaderTabMenuItem['id']
  label: HeaderTabMenuItem['label']
  path: string
  isActive?: (currentPath: string) => boolean
}

const DEFAULT_TABS: TabDefinition[] = [
  { id: 'visao-geral', label: 'Visão geral', path: '/' },
  {
    id: 'projetos',
    label: 'Projetos',
    path: '/projects',
    isActive: (currentPath) => {
      return currentPath === '/projects'
        || currentPath.startsWith('/simulation')
        || currentPath.startsWith('/customer')
        || currentPath.startsWith('/company')
        || currentPath.startsWith('/installation')
    },
  },
  { id: 'extrato', label: 'Extrato', path: '/bank-statement' },
]

const MY_WALLET_TAB: TabDefinition = { id: 'minha-carteira', label: 'Minha carteira', path: '/minha-carteira' }

const NEW_USERS_URL = 'https://fintech.solfacil.com.br/usuarios/novo'

function isTabActive(currentPath: string, tabPath: string) {
  if (tabPath === '/')
    return currentPath === '/'

  return currentPath === tabPath || currentPath.startsWith(`${tabPath}/`)
}

function safePush(router: ReturnType<typeof useRouter>, path: string) {
  const resolved = router.resolve(path)
  if (!resolved.matched.length) {
    // Route doesn't exist yet; ignore to avoid throwing.
    return
  }
  router.push(path)
}

interface Options {
  tabs?: TabDefinition[]
}

export default function useHeaderTabMenuItems(options: Options = {}) {
  const { isEnabled: isMyWalletEnabled } = useMyWalletAccess()

  // The "My wallet" tab is added only for who can access it (admin, or a partner the flag allows).
  const defaultTabs = computed<TabDefinition[]>(() => {
    if (!isMyWalletEnabled.value)
      return DEFAULT_TABS

    const statementIndex = DEFAULT_TABS.findIndex(tab => tab.id === 'extrato')
    return [...DEFAULT_TABS.slice(0, statementIndex), MY_WALLET_TAB, ...DEFAULT_TABS.slice(statementIndex)]
  })

  const route = useRoute()
  const router = useRouter()

  const tabMenuItems = computed<HeaderTabMenuItem[]>(() => {
    const currentPath = route.path

    const tabs = options.tabs ?? defaultTabs.value
    const baseItems = tabs.map(tab => ({
      id: tab.id,
      label: tab.label,
      active: tab.isActive ? tab.isActive(currentPath) : isTabActive(currentPath, tab.path),
      onClick: () => safePush(router, tab.path),
    }))

    baseItems.push({
      id: 'usuarios',
      label: 'Usuários',
      active: false,
      onClick: () => {
        window.open(NEW_USERS_URL, '_blank', 'noopener,noreferrer')
      },
    })

    return baseItems
  })

  return {
    tabMenuItems,
  }
}
